import React, { useState, useEffect } from 'react';
import { CustomGarage, Solicitud, Appointment, EvidenciaImg } from '../../types/ot.types';
import { OtService } from '../../services/OtService';
import { FileUploaderPreview } from '../FileUploaderPreview';

interface Step1Props {
    customGarages: CustomGarage[];
    usedCustomGarages: CustomGarage[];
    selectedCustomGarage: string;
    setSelectedCustomGarage: (val: string) => void;
    selectedGarage?: CustomGarage;
    setSelectedGarage: (garage?: CustomGarage) => void;
    appointments: Appointment[];
    stepOneFormAppointment: string;
    setStepOneFormAppointment: (val: string) => void;
    detalleSolicitud?: Solicitud;
    archivosRequerimiento: EvidenciaImg[];
    setArchivosRequerimiento: React.Dispatch<React.SetStateAction<EvidenciaImg[]>>;
    isCompleted?: boolean;
    minFechaCita?: string;
}

export const Step1Agendar: React.FC<Step1Props> = (props) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const usedGarages = props.usedCustomGarages || [];
    const nearGarages = props.customGarages || [];
    const apps = props.appointments || [];

    useEffect(() => {
        const delayDebounce = setTimeout(async () => {
            if (searchTerm != undefined && searchTerm.length >= 3 && !props.selectedGarage) {
                setIsSearching(true);
                setShowDropdown(true);
                const data = await OtService.searchTaller(searchTerm);
                let results = Array.isArray(data) ? [...data] : [];
                if (results.length === 0) {
                    results.push({ label: "No se encontraron talleres, clic para usar temporal.", value: "0" });
                } else {
                    results.push({ label: "Clic para usar taller temporal.", value: "0" });
                }
                setSearchResults(results);
                setIsSearching(false);
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm, props.selectedGarage]);

    const isValidBase64Browser = (str: string) => {
        try {
            atob(str);
            return true;
        } catch (e) {
            return false;
        }
    };

    const handleSelectTaller = (item: any) => {
        const garageName = item.value !== "0" ? item.label : "Centro temporal";
        setSearchTerm(garageName);
        props.setSelectedGarage({
            clave_centro: parseInt(isValidBase64Browser(item.value) ? atob(item.value) : item.value) || 0,
            clave_persona_proveedor: parseInt(isValidBase64Browser(item.proveedor) ? atob(item.proveedor) : item.proveedor) || 0,
            nombre_completo: item.name, sucursal: item.sucursal, phone: item.phone, dias_atencion: "", distancia: "", fecha_alta: new Date()
        });
        setShowDropdown(false);
    };

    const handleSelectTallerFromNear = (item: any) => {
        const garageName = item.clave_centro !== "0" ? item.sucursal : "Centro temporal";
        setSearchTerm(garageName);
        props.setSelectedGarage({
            clave_centro: parseInt(item.clave_centro),
            clave_persona_proveedor: parseInt(item.clave_persona_proveedor) || 0,
            nombre_completo: item.nombre_completo, sucursal: item.sucursal, phone: item.phone, dias_atencion: "", distancia: "", fecha_alta: new Date()
        });
        setShowDropdown(false);
    };

    const clearSearch = () => {
        setSearchTerm("");
        props.setSelectedGarage(undefined);
        setSearchResults([]);
        setShowDropdown(false);
    };

    const handleCustomTaller = (e: React.ChangeEvent<HTMLSelectElement, HTMLSelectElement>) => {
        props.setSelectedCustomGarage(e.target.value);
        if (e.target.value !== "1" && e.target.value !== "") {
            let customType = e.target.value.split("-");
            switch (customType[0]) {
                case "used":
                    handleSelectTallerFromNear(usedGarages.find((usedGarage) => usedGarage.clave_centro.toString() == customType[2]))
                    break;
                case "near":
                    handleSelectTallerFromNear(nearGarages.find((nearGarage) => nearGarage.clave_centro.toString() == customType[2]))
                    break;
                default:
                    break;
            }
        }
    };

    const formatearFecha = (fechaISO: string) => {
        if (!fechaISO) return "Sin asignar";
        const date = new Date(fechaISO);
        return date.toLocaleString('es-MX', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    if (props.isCompleted) {
        return (
            <div className="p-4 rounded border shadow-sm">
                <div className="row g-4 text-start align-items-center">
                    <div className="col-md-6 border-end">
                        <label className="fw-bold mb-2 text-primary text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                            <i className="fas fa-tools me-2"></i> Taller Asignado
                        </label>
                        <div className="fs-5 fw-bold mb-1">
                            {props.selectedGarage?.nombre_completo || "Taller no especificado"}
                        </div>
                        {props.selectedGarage?.sucursal && (
                            <span className="text-muted"><i className="fas fa-map-marker-alt me-1"></i> {props.selectedGarage.sucursal}</span>
                        )}
                    </div>

                    <div className="col-md-6">
                        <label className="fw-bold mb-2 text-primary text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                            <i className="fas fa-calendar-alt me-2"></i> Fecha de Cita Programada
                        </label>
                        <div className="fs-5 fw-bold text-capitalize">
                            {formatearFecha(props.stepOneFormAppointment)}
                        </div>
                    </div>

                    {props.archivosRequerimiento && props.archivosRequerimiento.length > 0 && (
                        <div className="col-12 mt-4 pt-3 border-top">
                            <FileUploaderPreview
                                label="Documento de Requerimiento / Solicitud"
                                archivos={props.archivosRequerimiento}
                                setArchivos={() => { }}
                                multiple={false}
                                disabled={true}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="row g-3 text-start">
            <div className="col-sm-6 col-lg-4" id="ot-step1-select-taller">
                <div className="form-group">
                    <label className="fw-bold mb-1">Seleccionar Taller</label>
                    <select
                        id="select_taller"
                        className="form-select shadow-sm"
                        required
                        value={props.selectedCustomGarage || "1"}
                        onChange={(e) => { handleCustomTaller(e) }}
                        disabled={props.isCompleted}
                    >
                        <option value="">Elegir...</option>
                        {usedGarages.length > 0 && (
                            <optgroup label="Últimos talleres">
                                {usedGarages.map(g => (
                                    <option key={`used-${g.clave_centro}`} value={`used-${g.clave_persona_proveedor}-${g.clave_centro}`}>
                                        {g.nombre_completo} {g.sucursal ? `- ${g.sucursal}` : ''}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                        {nearGarages.length > 0 && (
                            <optgroup label="Talleres cercanos">
                                {nearGarages.map(g => (
                                    <option key={`near-${g.clave_centro}`} value={`near-${g.clave_persona_proveedor}-${g.clave_centro}`}>
                                        {g.nombre_completo}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                        <optgroup label="Otros"><option value="1">Otro taller (Búsqueda)</option></optgroup>
                    </select>
                </div>
            </div>

            {(props.selectedCustomGarage === "1" || props.selectedCustomGarage === "") && (
                <div id="ot-step1-busqueda-taller" className="col-sm-6 col-lg-5">
                    <div className="form-group position-relative">
                        <label className="fw-bold mb-1">Búsqueda de taller</label>
                        <div className="input-group">
                            <span className="input-group-text bg-white"><i className="fas fa-search"></i></span>
                            <input type="search" className="form-control shadow-sm" placeholder="Nombre del proveedor..." value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); if (props.selectedGarage) props.setSelectedGarage(undefined); }}
                                onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                                readOnly={!!props.selectedGarage || props.isCompleted}
                                disabled={props.isCompleted}
                            />
                            {!props.isCompleted && props.selectedGarage && (
                                <button className="btn btn-outline-secondary" type="button" onClick={clearSearch}><i className="fas fa-times"></i></button>
                            )}
                        </div>

                        {showDropdown && (
                            <ul className="list-group position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>
                                {isSearching ? (
                                    <li className="list-group-item text-center text-muted"><i className="fas fa-spinner fa-spin me-2"></i> Buscando...</li>
                                ) : (
                                    searchResults.map((item, idx) => (
                                        <li key={idx} className="list-group-item list-group-item-action cursor-pointer" onClick={() => handleSelectTaller(item)}>
                                            {item.value === "0" ? <span className="text-primary fst-italic"><i className="fas fa-info-circle me-1"></i> {item.label}</span> : <span><i className="fas fa-tools me-2 text-muted"></i> {item.label}</span>}
                                        </li>
                                    ))
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            <div id="ot-step1-fecha-cita" className="col-sm-6 col-lg-3">
                <div className="form-group">
                    <label className="fw-bold mb-1">Fecha de cita</label>
                    <input
                        type="datetime-local"
                        className="form-control shadow-sm"
                        value={apps.length > 0 ? apps[0].url : (props.stepOneFormAppointment || "")}
                        onChange={(e) => props.setStepOneFormAppointment(e.target.value)}
                        disabled={props.isCompleted}
                        min={props.minFechaCita}
                    />
                </div>
            </div>

            <div id="ot-step1-requerimiento" className="col-12 col-lg-6 mt-4">
                <div className="form-group" style={props.isCompleted ? { pointerEvents: 'none', opacity: 0.7 } : {}}>
                    <FileUploaderPreview
                        label="Opcional: Formato de requerimiento"
                        archivos={props.archivosRequerimiento || []}
                        setArchivos={props.setArchivosRequerimiento}
                        multiple={false}
                    />
                    <small className="text-muted d-block mt-1">
                        Puede adjuntar un documento PDF o imagen con los requisitos previos del taller.
                    </small>
                </div>
            </div>
        </div>
    );
};